import { refreshToken } from "@website/fetcher/refresh-token";
import {
  LoginStatus,
  applicationStore,
  authStore,
  useSelector
} from "@website/store/store";
import { getCookie, removeCookie, setCookie } from "@website/utils/cookie";
import jwtDecode from "jwt-decode";
import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  LEGACY_ACCESS_TOKEN_KEY,
  LEGACY_REFRESH_TOKEN_KEY,
  REFRESH_BUFFER_MS,
  REFRESH_TOKEN_STORAGE_KEY,
  TOKEN_STORAGE_KEY
} from "./constants";

import { setTracking } from "@website/store/tracking";
import { uuid } from "@website/utils/uuid";
import {
  captureTokenError,
  getLegacyTokens,
  getTokenCookieMaxAgeSeconds,
  getTokenWithRefresh,
  isTokenValid,
  removeTokenFromUrl,
  updateTokenVersion
} from "./helpers";
import { convert } from "./request";
import { DECODED_TOKEN, SetTokenProperties } from "./types";

const tokenHelper = {
  get: (): { stToken: string; refreshToken: string } => ({
    stToken: getCookie(TOKEN_STORAGE_KEY) || "",
    refreshToken: getCookie(REFRESH_TOKEN_STORAGE_KEY) || ""
  }),

  set: (data: SetTokenProperties): void => {
    const { accessToken, refreshToken } = data;
    const tokenMaxAge = getTokenCookieMaxAgeSeconds(
      accessToken,
      REFRESH_BUFFER_MS
    );

    const refreshTokenMaxAge = refreshToken
      ? getTokenCookieMaxAgeSeconds(refreshToken)
      : undefined;

    const canPersistAccessToken = isTokenValid(accessToken, tokenMaxAge);
    const canPersistRefreshToken = isTokenValid(
      refreshToken,
      refreshTokenMaxAge
    );

    if (!canPersistAccessToken && !canPersistRefreshToken) {
      authStore.loginStatus.set(LoginStatus.LoggedOut);
      return;
    }
    if (canPersistAccessToken) {
      setCookie(TOKEN_STORAGE_KEY, accessToken, {
        path: "/",
        maxAge: tokenMaxAge
      });
      authStore.loginStatus.set(LoginStatus.Success);
    }
    if (canPersistRefreshToken) {
      setCookie(REFRESH_TOKEN_STORAGE_KEY, refreshToken, {
        path: "/",
        maxAge: refreshTokenMaxAge
      });
    }
    if (canPersistAccessToken || canPersistRefreshToken) {
      updateTokenVersion();
    }
  },

  delete: (): void => {
    removeCookie(TOKEN_STORAGE_KEY, { path: "/" });
    removeCookie(REFRESH_TOKEN_STORAGE_KEY, { path: "/" });
    authStore.loginStatus.set(LoginStatus.LoggedOut);
  },

  migrateFromLocalStorage: (data: SetTokenProperties) => {
    tokenHelper.set(data);
  }
};

export const logout = () => {
  tokenHelper.delete();
  applicationStore.shopping.delete();
  if (typeof window !== "undefined") {
    authStore.accessToken.delete();
    authStore.refreshToken.delete();
    localStorage.removeItem(LEGACY_ACCESS_TOKEN_KEY);
    localStorage.removeItem(LEGACY_REFRESH_TOKEN_KEY);
    authStore.delete();
    localStorage.removeItem("auth_store");
    setTracking((previous) => ({ ...previous, sid: uuid() }));
  }
};

export const saveLogin = (data: SetTokenProperties) => {
  tokenHelper.set(data);
};

export const useJwtToken = (): DECODED_TOKEN => {
  const tokenVersion = useSelector(authStore.tokenVersion);
  const token = tokenHelper.get().stToken ?? "";
  const fallback: DECODED_TOKEN = useMemo(
    () => ({
      analyticsId: "",
      email: "",
      exp: 0,
      iat: 0,
      name: "",
      phoneNumber: "",
      sub: "",
      userPushId: "",
      walletUsername: "",
      customerKey: "",
      profileType: "B2C"
    }),
    []
  );
  return useMemo(() => {
    if (!token) return fallback;
    try {
      return jwtDecode(token) || fallback;
    } catch {
      captureTokenError("token could  not  be decoded");
      return fallback;
    }
  }, [fallback, token, tokenVersion]);
};

export const useLoginStatus = () => {
  const loginStatus = useSelector(authStore.loginStatus);
  return {
    isLoggedIn: loginStatus === LoginStatus.Success,
    loginStatus
  };
};
export const getToken = () => tokenHelper.get();

export const useAuthSessionRefresh = ({
  routeChangeKey,
  queryToken
}: {
  routeChangeKey: string;
  queryToken: string;
}) => {
  const hasMounted = useRef(false);
  const { loginStatus } = useLoginStatus();
  const shouldSkipRefresh =
    loginStatus === LoginStatus.Pending || Boolean(queryToken);

  const refreshSession = useCallback(async () => {
    if (shouldSkipRefresh) return;

    const token = await getTokenWithRefresh();

    if (
      !token.stToken &&
      !token.refreshToken &&
      loginStatus !== LoginStatus.LoggedOut
    ) {
      logout();
    }
  }, [shouldSkipRefresh, loginStatus]);

  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }

    refreshSession();
  }, [routeChangeKey, shouldSkipRefresh]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      refreshSession();
    };

    const handleFocus = () => {
      refreshSession();
    };

    window.addEventListener("focus", handleFocus);
    window.addEventListener("pageshow", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("pageshow", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [refreshSession]);
};

const refreshAccessToken = async (loginWebengage: () => void) => {
  try {
    await refreshToken();
    loginWebengage();
  } catch {
    authStore.loginStatus.set(LoginStatus.LoggedOut);
  }
};
export const convertToken = async (
  queryToken: string,
  loginWebengage: () => void
) => {
  const { stToken: cookieToken, refreshToken: cookieRefreshToken } =
    tokenHelper.get();
  if (cookieToken) {
    loginWebengage();
    authStore.loginStatus.set(LoginStatus.Success);
    updateTokenVersion();
    return;
  }
  if (queryToken) {
    try {
      authStore.loginStatus.set(LoginStatus.Pending);
      await convert(queryToken);
      removeTokenFromUrl();
      loginWebengage();
      return;
    } catch {
      authStore.loginStatus.set(LoginStatus.LoggedOut);
      if (typeof window !== "undefined") {
        captureTokenError("snapp token conversion failed");
      }
      return;
    }
  }
  if (cookieRefreshToken) {
    refreshAccessToken(loginWebengage);
    return;
  }
  if (typeof window !== "undefined") {
    const { accessToken: accessFromLocal, refreshToken: refreshFromLocal } =
      getLegacyTokens();

    if (accessFromLocal || refreshFromLocal) {
      if (accessFromLocal) {
        tokenHelper.migrateFromLocalStorage({
          accessToken: accessFromLocal,
          refreshToken: refreshFromLocal
        });
        loginWebengage();
        return;
      }
      tokenHelper.migrateFromLocalStorage({
        accessToken: "",
        refreshToken: refreshFromLocal
      });
      refreshAccessToken(loginWebengage);

      return;
    }
  }
  authStore.loginStatus.set(LoginStatus.LoggedOut);
};
